// @flow

import * as React from 'react';
import {defineMessages, FormattedMessage} from 'react-intl';
import {ContentGroupItem} from 'docs/util';
import {Table, TableHeaderCell, TableCell} from '../../core/Table/Table';
import {InlineCode} from '../../core/InlineCode/InlineCode';
import {TabGroup} from '../../core/Tabs/TabGroup';

const messages = defineMessages({
  immediateSuccess: {
    id: 'docsShell.sepaTestAccountTable.immediateSuccess',
    description: 'Describes a successful payment test case',
    defaultMessage:
      'The PaymentIntent status transitions from <InlineCode>processing</InlineCode> to <InlineCode>succeeded</InlineCode>.',
  },
  delayedSuccess: {
    id: 'docsShell.sepaTestAccountTable.delayedSuccess',
    description: 'Describes a delayed successful payment test case',
    defaultMessage:
      'The PaymentIntent status transitions from <InlineCode>processing</InlineCode> to <InlineCode>succeeded</InlineCode> after three minutes.',
  },
  immediateFailure: {
    id: 'docsShell.sepaTestAccountTable.immediateFailure',
    description: 'Describes a failed test payment case',
    defaultMessage:
      'The PaymentIntent status transitions from <InlineCode>processing</InlineCode> to <InlineCode>requires_payment_method</InlineCode>.',
  },
  delayedFailure: {
    id: 'docsShell.sepaTestAccountTable.delayedFailure',
    description: 'Describes a delayed failed test payment case',
    defaultMessage:
      'The PaymentIntent status transitions from <InlineCode>processing</InlineCode> to <InlineCode>requires_payment_method</InlineCode> after three minutes.',
  },
  dispute: {
    id: 'docsShell.sepaTestAccountTable.dispute',
    description: 'Describes a disputed test payment',
    defaultMessage:
      'The PaymentIntent status transitions from <InlineCode>processing</InlineCode> to <InlineCode>succeeded</InlineCode>, but a dispute is immediately created.',
  },
  accountHeader: {
    id: 'docsShell.sepaTestAccountTable.accountHeader',
    description: 'A header used in a table',
    defaultMessage: 'Account Number',
  },
  descriptionHeader: {
    id: 'docsShell.sepaTestAccountTable.descriptionHeader',
    description: 'A header used in a table',
    defaultMessage: 'Description',
  },
});

const InlineCodeComponent = (label) => <InlineCode>{label}</InlineCode>;

const SepaTestAccountStates: {
  [state: string]: React.Element<typeof React.Fragment>,
} = {
  immediate_success: (
    <FormattedMessage
      {...messages.immediateSuccess}
      values={{
        InlineCode: InlineCodeComponent,
      }}
    />
  ),
  delayed_success: (
    <FormattedMessage
      {...messages.delayedSuccess}
      values={{
        InlineCode: InlineCodeComponent,
      }}
    />
  ),
  immediate_failure: (
    <FormattedMessage
      {...messages.immediateFailure}
      values={{
        InlineCode: InlineCodeComponent,
      }}
    />
  ),
  delayed_failure: (
    <FormattedMessage
      {...messages.delayedFailure}
      values={{
        InlineCode: InlineCodeComponent,
      }}
    />
  ),
  dispute: (
    <FormattedMessage
      {...messages.dispute}
      values={{
        InlineCode: InlineCodeComponent,
      }}
    />
  ),
};

type SepaTestAccountStateType = $Keys<typeof SepaTestAccountStates>;

type SepaTestAccountTableEntryType = {|
  country: {name: string, code: string},
  numbers: {[state: SepaTestAccountStateType]: string},
|};

const SepaTestAccountTableEntry = ({
  entry,
}: {
  entry: SepaTestAccountTableEntryType,
}) => {
  return (
    <Table type="striped">
      <thead>
        <tr>
          <TableHeaderCell>
            <FormattedMessage {...messages.accountHeader} />
          </TableHeaderCell>
          <TableHeaderCell>
            <FormattedMessage {...messages.descriptionHeader} />
          </TableHeaderCell>
        </tr>
      </thead>
      <tbody>
        {Object.entries(SepaTestAccountStates).map(
          ([state, frag]: [string, any]) => (
            <tr>
              <TableCell>
                <InlineCode>{entry.numbers[state]}</InlineCode>
              </TableCell>
              <TableCell>{frag}</TableCell>
            </tr>
          ),
        )}
      </tbody>
    </Table>
  );
};

type SepaTestAccountTableProps = {|
  entries: Array<SepaTestAccountTableEntryType>,
  children?: React.Node,
|};

export const SepaTestAccountTable = ({entries}: SepaTestAccountTableProps) => {
  if (!entries) {
    return null;
  }
  const items = entries.map(
    (entry: SepaTestAccountTableEntryType) =>
      new ContentGroupItem(
        entry.country.code,
        entry.country.name,
        (<SepaTestAccountTableEntry entry={entry} />),
      ),
  );

  return <TabGroup items={items} selector="drop-down" />;
};
